import {Box, Button, CircularProgress, Paper} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PageSubtitle from '../shared/PageSubtitle';
import LoadGraph from '../shared/LoadGraph';
import {useTranslation} from 'react-i18next';
import {getMean} from '../../services/mathService';
import {AcwrScore} from '../../interfaces';
import {getTeam} from '../../api/reportAPI';
import {DateTime} from 'luxon';
import {Positions} from '../../enums';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormDateRange from '../shared/FormDateRange';
import {getXAxisTickPeriod} from '../../services/graphService';

const getAcwrDataObj = (acwrScores: AcwrScore[]) => {
  return acwrScores.reduce(
    (acc, curr) => {
      if (!acc[curr.date as string]) {
        acc[curr.date as string] = [];
      }
      acc[curr.date as string].push(curr);
      return acc;
    },
    {} as Record<string, AcwrScore[]>,
  );
};

const getAcwrDataArray = (
  acwrDataObj: Record<string, AcwrScore[]>,
): {
  date: string;
  acuteWorkload: number | null;
  chronicWorkload: number | null;
  acwrScore: number | null;
}[] => {
  return Object.keys(acwrDataObj)
    .sort()
    .map(date => ({
      date,
      acwrScore: getMean(
        acwrDataObj[date].map(acwr => acwr.acwrScore as number),
      ),
      acuteWorkload: getMean(
        acwrDataObj[date].map(acwr => acwr.acuteWorkload as number),
      ),
      chronicWorkload: getMean(
        acwrDataObj[date].map(acwr => acwr.chronicWorkload as number),
      ),
    }))
    .map(d => ({
      date: d.date,
      acuteWorkload:
        d.acuteWorkload !== null ? parseFloat(d.acuteWorkload.toFixed(2)) : 0,
      chronicWorkload:
        d.chronicWorkload !== null
          ? parseFloat(d.chronicWorkload.toFixed(2))
          : 0,
      acwrScore: d.acwrScore !== null ? parseFloat(d.acwrScore.toFixed(2)) : 0,
    }));
};

const getFormattedAcwrDataArray = (
  acwrScores: AcwrScore[],
  allDates?: string[],
) => {
  const acwrDataObj = getAcwrDataObj(acwrScores);
  const acwrDataArray = getAcwrDataArray(acwrDataObj);
  // if allDates is provided, add missing dates to the array
  if (allDates) {
    const missingDates = allDates.filter(
      date => !acwrDataArray.some(d => d.date === date),
    );
    missingDates.forEach(date => {
      acwrDataArray.push({
        date,
        acuteWorkload: null,
        chronicWorkload: null,
        acwrScore: null,
      });
    });
  }
  // sort by date
  return acwrDataArray.sort((a, b) => a.date.localeCompare(b.date));
};

interface TeamLoadReportForm {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
    key: 'selection';
  } | null;
}

interface Props {
  teamName: string;
  teamId: number;
}

const TeamLoad: React.FC<Props> = ({teamName, teamId}) => {
  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const [allAcwrDataArray, setAllAcwrDataArray] = useState<
    {
      date: string;
      acuteWorkload: number | null;
      chronicWorkload: number | null;
      acwr: number | null;
    }[]
  >([]);
  const [forwardAcwrDataArray, setForwardAcwrDataArray] = useState<
    {
      date: string;
      acuteWorkload: number | null;
      chronicWorkload: number | null;
      acwr: number | null;
    }[]
  >([]);
  const [defenseAcwrDataArray, setDefenseAcwrDataArray] = useState<
    {
      date: string;
      acuteWorkload: number | null;
      chronicWorkload: number | null;
      acwr: number | null;
    }[]
  >([]);

  const fetchData = useCallback(
    async (startDate: Date, endDate: Date) => {
      setLoading(true);
      const res = await getTeam({
        id: teamId,
        acwrScoreStartDate:
          DateTime.fromJSDate(startDate).toFormat('yyyy-MM-dd'),
        acwrScoreEndDate: DateTime.fromJSDate(endDate).toFormat('yyyy-MM-dd'),
      });

      const allAcwrData = (res?.players ?? [])
        .flatMap(player => player?.profile?.acwrScores ?? [])
        .filter(
          acwr =>
            acwr?.acwrScore && acwr?.acuteWorkload && acwr?.chronicWorkload,
        );

      const forwardAcwrData = (res?.players ?? [])
        .filter(
          player =>
            player?.profile?.position === Positions.center ||
            player?.profile?.position === Positions.leftwing ||
            player?.profile?.position === Positions.rightwing,
        )
        .flatMap(player => player.profile?.acwrScores ?? [])
        .filter(
          acwr => acwr.acwrScore && acwr.acuteWorkload && acwr.chronicWorkload,
        );

      const defenseAcwrData = (res?.players ?? [])
        .filter(player => player?.profile?.position === Positions.defense)
        .flatMap(player => player?.profile?.acwrScores ?? [])
        .filter(
          acwr => acwr.acwrScore && acwr.acuteWorkload && acwr.chronicWorkload,
        );

      // group allAcwrData by date
      const allAcwrDataArray = getFormattedAcwrDataArray(allAcwrData);
      const allDates = allAcwrDataArray.map(d => d.date);
      const forwardAcwrDataArray = getFormattedAcwrDataArray(
        forwardAcwrData,
        allDates,
      );
      const defenseAcwrDataArray = getFormattedAcwrDataArray(
        defenseAcwrData,
        allDates,
      );
      setAllAcwrDataArray(
        allAcwrDataArray.map(d => ({
          ...d,
          acwr: d.acwrScore,
        })),
      );
      setForwardAcwrDataArray(
        forwardAcwrDataArray.map(d => ({
          ...d,
          acwr: d.acwrScore,
        })),
      );
      setDefenseAcwrDataArray(
        defenseAcwrDataArray.map(d => ({
          ...d,
          acwr: d.acwrScore,
        })),
      );
      setLoading(false);
    },
    [teamId],
  );

  const {handleSubmit, watch, control} = useForm<TeamLoadReportForm>({
    defaultValues: {
      dateRange: {
        key: 'selection',
        startDate: DateTime.fromJSDate(new Date()).minus({days: 90}).toJSDate(),
        endDate: new Date(),
      },
    },
  });

  // fetch team data from api on page load
  // used for load info on players
  useEffect(
    () => {
      fetchData(
        watch('dateRange')?.startDate as Date,
        watch('dateRange')?.endDate as Date,
      );
    },
    // we don't want to fetch data on every change of watch
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData, teamId],
  );

  const xAxisTickPeriod = useMemo(() => {
    return getXAxisTickPeriod(allAcwrDataArray.length);
  }, [allAcwrDataArray]);

  const handleSubmitCallback = useCallback(
    async (data: TeamLoadReportForm) => {
      fetchData(
        data.dateRange?.startDate as Date,
        data.dateRange?.endDate as Date,
      );
    },
    [fetchData],
  );

  return (
    <Paper sx={{p: 5, mb: 5}}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}>
        <PageSubtitle sx={{mt: 0}}>{t('statistics.playerLoad')}</PageSubtitle>
        <form onSubmit={handleSubmit(handleSubmitCallback)}>
          <Box sx={{display: 'flex', flexDirection: 'row', gap: 2}}>
            <FormDateRange
              label={t('sessionFilter.sessionDates') as string}
              control={control as unknown as Control<FieldValues>}
              name="dateRange"
              sx={{minWidth: 200}}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!watch('dateRange') || loading}>
              {t('general.submit')}
            </Button>
          </Box>
        </form>
      </Box>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}>
          <CircularProgress />
        </Box>
      )}
      {(allAcwrDataArray ?? []).length > 0 && !loading && (
        <>
          <LoadGraph
            title={t('position.all')}
            loadHistory={allAcwrDataArray}
            showLegend={false}
            height={200}
            showDownloadButton
            teamName={teamName}
            reportName={`${t('statistics.playerLoad')} ${t('position.all')}`}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        </>
      )}
      {(forwardAcwrDataArray ?? []).length > 0 && !loading && (
        <>
          <LoadGraph
            title={t('position.forwards')}
            loadHistory={forwardAcwrDataArray}
            showLegend={false}
            height={200}
            showDownloadButton
            teamName={teamName}
            reportName={`${t('statistics.playerLoad')} ${t('position.forwards')}`}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        </>
      )}
      {(defenseAcwrDataArray ?? []).length > 0 && !loading && (
        <>
          <LoadGraph
            title={t('position.defense')}
            loadHistory={defenseAcwrDataArray}
            showLegend
            height={200}
            showDownloadButton
            teamName={teamName}
            reportName={`${t('statistics.playerLoad')} ${t('position.defense')}`}
            xAxisTickPeriod={xAxisTickPeriod}
          />
        </>
      )}
    </Paper>
  );
};

export default TeamLoad;
